import React, { useEffect, useState } from 'react'
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs'
import Hero from '../../components/Hero/Hero'
import SearchBar from '../../components/SearchBar/SearchBar'
import Filters, { ICategoryProps } from '../../components/Filters/Filters'
import MediaAndContentMapperResearch from '../../components/Mappers/MediaAndContentMapperResearch'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { getResearchs, getCategories } from '../../redux/slices/researchs/researchsThunk'
import Loading from '../../components/Loading/Loading'
import Pagination from '../../components/Pagination/Pagination'
import { useLocation, useNavigate } from 'react-router-dom'
import ResponsiveContainer from '../../layouts/ResponsiveContainer'
import SEO from '../../components/SEO/SEO'
import { clearResearchs } from '../../redux/slices/researchs/researchsSlice'
import withoutPubli from '../../assets/img/IMG inv y Acciones.png'
import { scrollToElement } from '../../utils/utils'
import ContactSection from '../../components/Common/ContactSection'

const DEFAULT_MIN_DATE = '2024-01-01';

const Index = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    // Obtención de datos del estado global de Redux
    const { researchs, pagination, loading, categoriesOptions } = useAppSelector((state) => state.researchs)
    const { currentPage, totalPages, totalItems } = pagination;

    // Estados locales para filtros y fechas
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [categorieSelected, setCategorieSelected] = useState<string>('');
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [disabledPeriodFrom, setDisabledPeriodFrom] = useState<boolean>(false);
    const [disabledPeriodTo, setDisabledPeriodTo] = useState<boolean>(true);
    const [minPeriodDateFrom, _setMinPeriodDateFrom] = useState<string>(DEFAULT_MIN_DATE);
    const [minPeriodDateTo, setMinPeriodDateTo] = useState<string>('');
    const [maxPeriodDateFrom, setMaxPeriodDateFrom] = useState<string>('');
    const [maxPeriodDateTo, setMaxPeriodDateTo] = useState<string>('');
    const [disabledSearch, setDisabledSearch] = useState<boolean>(true);
    const [disabledCategories, setDisabledCategories] = useState<boolean>(false);
    const [cleanValue, setCleanValue] = useState<boolean>(false);

    // Efecto para cargar categorías y establecer límite de fecha máximo
    useEffect(() => {
        if (!categoriesOptions?.length) {
            dispatch(getCategories())
        }
        maxDateLimit();
    }, [categoriesOptions?.length])

    useEffect(() => {
        const query = params.get('search') || '';
        const page = parseInt(params.get('page') || '1', 10);
        const categorie = params.get('categorie') || '';
        const dateTo = params.get('to') || "";
        const dateFrom = params.get('from') || "";
        const id = params.get('id') || "";
        const related_indicator_id = params.get('related_indicator_id') || '';

        if (id) {
            dispatch(getResearchs({ id, page: 1 }));
            return;
        }

        setCategorieSelected(categorie);
        setDateFrom(dateFrom);

        if (dateTo) {
            setDateTo(dateTo);
            setDisabledPeriodTo(false);
        }

        if (query) {
            setSearchQuery(query);
            setCleanValue(true);
            setDisabledSearch(true);
        } 
        
        dispatch(getResearchs({
            categoria: categorie,
            page,
            search: query,
            fechaTo: dateTo,
            fechaFrom: dateFrom,
            related_indicator: related_indicator_id
        }));

    }, [dispatch, location.search]);

    useEffect(() => {
        const noResearchs = !researchs || researchs.length === 0;
        const noCategories = !categorieSelected;

        const isDisabled = noResearchs && noCategories;

        setDisabledPeriodFrom(isDisabled);
        setDisabledCategories(isDisabled);
    }, [researchs, categorieSelected]);

    useEffect(() => {
        const id = params.get('id');

        if (id && researchs && researchs.length > 0 && loading === 'succeeded') {
            scrollToElement(`researchs`, -120, 1000);
        }
    }, [researchs, loading]);

    useEffect(() => {
        return () => {
            dispatch(clearResearchs())
        }
    }, [])

    // Manejo de cambio de página en la paginación
    const handlePageChange = (page: number) => {
        const params = new URLSearchParams(location.search);
        searchQuery && params.set('search', searchQuery);
        params.set('page', page.toString());
        navigate({ search: params.toString() });
    };

    // Manejo de búsqueda
    const handleSearch = (query: string) => {
        if (query) {
            const params = new URLSearchParams(location.search);
            setSearchQuery(query);
            setCleanValue(true);
            setDisabledSearch(true);
            query ? params.set('search', query) : params.delete('search');
            params.set('page', '1');
            navigate({ search: params.toString() });
        }
    };

    const onSelect = (value: string) => {
        const params = new URLSearchParams(location.search);
        setCategorieSelected(value !== 'Todas' ? value : '');
        value !== 'Todas' ? params.set('categorie', value) : params.delete('categorie');
        params.set('page', '1');
        navigate({ search: params.toString() });
    };

    const onHandleChangePeriodFrom = (e: React.ChangeEvent<HTMLInputElement>) => {
        const date = e.target.value;
        const params = new URLSearchParams(location.search);
        if (date === "") {
            setDisabledPeriodTo(true);
            setDateTo('');
            setDateFrom('');
            params.delete('from');
            params.delete('to');
        } else {
            setDateFrom(date);
            setDateTo('');
            setDisabledPeriodTo(false);
            setMinPeriodDateTo(e.target.value)
        }
        navigate({ search: params.toString() });
    }

    const onHandleChangePeriodTo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const dateTo = e.target.value;
        const params = new URLSearchParams(location.search);
        if (dateTo !== '') {
            setDateTo(e.target.value);
            params.set('from', dateFrom);
            params.set('to', dateTo);
            params.set('page', '1');
        } else {
            setDateTo('');
            setDateFrom('');
            params.delete('from');
            params.delete('to');
        }
        navigate({ search: params.toString() });
    }

    const maxDateLimit = () => {
        const currentDay = new Date().toISOString().slice(0, 10);
        setMaxPeriodDateFrom(currentDay);
        setMaxPeriodDateTo(currentDay);
    };

    const onClearSearchBar = () => {
        setSearchQuery('');
        setDisabledSearch(true);
        setCleanValue(false);
        if (!params.has('search')) return;
        params.delete('search');
        navigate({ search: params.toString() });
    }

    const handleClearFilters = () => {
        const params = new URLSearchParams(location.search);
        setCategorieSelected('');
        setDateFrom('');
        setDateTo('');
        setDisabledPeriodTo(true);
        params.delete('categorie');
        params.delete('from');
        params.delete('to');
        params.set('page', '1');
        navigate({ search: params.toString() });
    };

    const dateProps = {
        onHandleChangePeriodFrom,
        onHandleChangePeriodTo,
        disabledPeriodFrom,
        disabledPeriodTo,
        minPeriodDateFrom,
        maxPeriodDateFrom,
        minPeriodDateTo,
        maxPeriodDateTo,
        valueDateFrom: dateFrom,
        valueDateTo: dateTo,
    };

    const categoryProps: ICategoryProps = {
        options: categoriesOptions ? categoriesOptions : [],
        numberOfArticles: researchs && loading === 'pending' && researchs?.length === 0 ? 0 : totalItems,
        onSelect,
        selectedItem: categorieSelected,
        defaultSelectedItem: 'Todas',
        label: 'Categoría',
        disabledCategories,
        variant: 'outline-green'
    };

    return (
        <>
            <SEO
                title='Informes'
                description='Informes realizados por el monitor de derechos de CABA'
                type='article'
            />
            <Hero
                breadcrumbs={<Breadcrumbs />}
                title='Informes'
                paragraph='Acá vas a encontrar las investigaciones y estudios que desarrollamos sobre los derechos monitoreados y otros trabajos más específicos, con la mirada puesta en relevar vulneraciones sistemáticas y efectuar recomendaciones de política pública.'
                media={{ type: '', src: '', url: '', alt: '', className: '' }}
                layout='row'
                containerHeightClassName='h-full'
                backgroundClassName={{ backgroundColor: '#E3DDD8' }}
                titleClassName='text-brand-green-20'
                contentContainerClassName={'!py-[3.25rem]'}
            />
            <div className='flex flex-col items-center'>
                <ResponsiveContainer className='flex flex-col justify-center' >
                    <div className='mt-16'>
                        <SearchBar
                            placeholder='Buscar informes'
                            onSearch={handleSearch}
                            defaultValue={searchQuery}
                            disabledSearch={disabledSearch}
                            onClear={onClearSearchBar}
                            cleanValue={cleanValue}
                        />
                        <Filters
                            dateProps={dateProps}
                            categoryProps={categoryProps}
                            onClearFilters={handleClearFilters}
                        />
                    </div>
                    {loading === 'pending' || (loading === 'succeeded' && researchs === null) ? (
                        <Loading
                            size={40}
                            className='h-[300px]'
                        />
                    ) : (
                        <>
                            {researchs && researchs?.length === 0 ? (
                                <div className="flex items-center justify-center mt-12 mb-2">
                                    <div className='w-full sm:w-[536px] flex items-center flex-col'>
                                        <p className='text-blarge text-system-light-50 text-center mb-5'>
                                            {categorieSelected || dateFrom || searchQuery
                                                ? "No hay informes encontrados"
                                                : "Aún no hay publicaciones en esta sección, pero muy pronto compartiremos investigaciones y análisis sobre temas clave."
                                            }
                                        </p>
                                        <img src={withoutPubli} alt="Sin publicaciones" />
                                    </div>
                                </div>
                            ) : (
                                <div id='researchs'>
                                    <MediaAndContentMapperResearch
                                        data={researchs}
                                    />
                                    <Pagination
                                        data={researchs || []}
                                        totalPages={totalPages}
                                        currentPage={currentPage}
                                        setCurrentPage={handlePageChange}
                                        scrollTargetId='researchs'
                                    />
                                </div>
                            )}
                        </>
                    )}
                </ResponsiveContainer>
                <ContactSection />
            </div>
        </>
    )
}

export default Index